// App Hints - JSON data-driven for scalability
import hintsData from "./appHints.data.json";

/**
 * Normalize image slug/name for matching
 * Handles formats like:
 * - "wordpress-ubuntu-22-04" (slug format)
 * - "WordPress on Ubuntu 22.04" (natural language)
 * - "CyberPanel 2.3 on Ubuntu 22.04"
 * - String directly: "WordPress on Ubuntu 22.04"
 * - Object: { name: "WordPress on Ubuntu 22.04", slug: "wordpress-20-04" }
 * @param {string|object} image - Image string or object from server
 * @returns {string} Normalized slug
 */
function normalizeSlug(image) {
  // Handle both string and object formats
  let raw = "";
  if (typeof image === "string") {
    raw = image;
  } else if (image && typeof image === "object") {
    raw = image?.slug || image?.name || image?.description || "";
  }

  const normalized = String(raw).toLowerCase().trim();

  // Remove common separators and normalize
  return normalized
    .replace(/\s+on\s+/gi, " ") // Remove "on" separator
    .replace(/\s+with\s+/gi, " ") // Remove "with" separator
    .replace(/\s+for\s+/gi, " ") // Remove "for" separator
    .replace(/\s+\d+[\d.]*\s*/g, " ") // Remove version numbers
    .replace(/\s*\(.*?\)\s*/g, " ") // Remove parentheses content
    .replace(/[^a-z0-9\s-]/g, " ") // Keep only alphanumeric, spaces, and hyphens
    .replace(/\s+/g, " ") // Normalize multiple spaces
    .trim();
}

/**
 * Extract potential app keywords from image name/slug
 * Returns an array of potential matches to check against app keys
 * @param {string} normalized - Normalized image string
 * @returns {Array<string>} Potential app keywords
 */
function extractAppKeywords(normalized) {
  const keywords = [];

  // Split by common delimiters
  const parts = normalized.split(/[\s-_]+/).filter((part) => part.length > 2); // Ignore very short parts

  // Add individual parts
  keywords.push(...parts);

  // Add multi-word combinations (for apps like "ruby on rails" -> "ruby rails")
  if (parts.length > 1) {
    // First two words
    keywords.push(parts.slice(0, 2).join(" "));
    keywords.push(parts.slice(0, 2).join(""));

    // First three words if available
    if (parts.length > 2) {
      keywords.push(parts.slice(0, 3).join(" "));
    }
  }

  // Add the full normalized string
  keywords.push(normalized);

  // Remove duplicates and empty strings
  return [...new Set(keywords)].filter((k) => k && k.length > 0);
}

/**
 * Find matching app hint key for an image
 * Uses smart matching with keywords extraction and special case handling
 * @param {string} slug - Normalized image slug
 * @returns {string|null} Matching key from hintsData
 */
function findMatchingApp(slug) {
  if (!slug) return null;

  const keys = Object.keys(hintsData);
  const keywords = extractAppKeywords(slug);

  // Special cases for common variations and aliases
  const specialCases = {
    wp: "wordpress",
    wpress: "wordpress",
    mariadb: "mysql",
    node: "nodejs",
    nodejs: "nodejs",
    rails: "ruby",
    ror: "ruby",
    rubyonrails: "ruby",
    mongo: "mongodb",
    postgres: "postgresql",
    pgsql: "postgresql",
    pg: "postgresql",
    es: "elasticsearch",
    elastic: "elasticsearch",
    k8s: "kubernetes",
    kube: "kubernetes",
    vpn: "openvpn",
    wiki: "mediawiki",
    mw: "mediawiki",
    chat: "rocketchat",
    mail: "mailcow",
    php: "lamp",
    phpmyadmin: "phpmyadmin",
    pma: "phpmyadmin",
    next: "nextcloud",
    owncloud: "owncloud",
    nc: "nextcloud",
    oc: "owncloud",
    rocket: "rocketchat",
    matter: "mattermost",
    git: "gitlab",
    gogs: "gitea",
    grafana: "grafana",
    prom: "prometheus",
  };

  // 1. Try exact match with app keys
  for (const keyword of keywords) {
    const exactMatch = keys.find((k) => keyword === k);
    if (exactMatch) return exactMatch;
  }

  // 2. Try special cases mapping
  for (const keyword of keywords) {
    for (const [pattern, target] of Object.entries(specialCases)) {
      if (keyword === pattern && keys.includes(target)) {
        return target;
      }
    }
  }

  // 3. Try word boundary matching (more accurate than simple contains)
  // Prioritize longer app keys to avoid false positives (e.g., "redis" vs "redmine")
  const sortedKeys = [...keys].sort((a, b) => b.length - a.length);

  for (const key of sortedKeys) {
    // Create regex for word boundary matching
    const regex = new RegExp(`\\b${key}\\b`, "i");

    for (const keyword of keywords) {
      if (regex.test(keyword)) {
        return key;
      }
    }

    // Also check if keyword is at the start
    for (const keyword of keywords) {
      if (keyword.startsWith(key) || key.startsWith(keyword)) {
        // Additional check: ensure it's a meaningful match
        const matchRatio =
          Math.min(key.length, keyword.length) /
          Math.max(key.length, keyword.length);
        if (matchRatio > 0.7) {
          // 70% similarity threshold
          return key;
        }
      }
    }
  }

  // 4. Try contains match with full normalized slug (fallback)
  const containsMatch = sortedKeys.find((k) => slug.includes(k));
  if (containsMatch) return containsMatch;

  // 5. Last resort: check special cases with contains
  for (const [pattern, target] of Object.entries(specialCases)) {
    if (slug.includes(pattern) && keys.includes(target)) {
      return target;
    }
  }

  return null;
}

/**
 * Get quick access links for an application
 * @param {string|object} image - Image string or object from server
 * @param {string} ipAddress - Server IP address
 * @returns {Array} Array of link objects
 */
export function getAppQuickLinks(image, ipAddress) {
  if (!image || !ipAddress) return [];

  const slug = normalizeSlug(image);
  const matchedKey = findMatchingApp(slug);

  if (!matchedKey) return [];

  const appData = hintsData[matchedKey];
  if (!appData || !appData.links) return [];

  try {
    return appData.links.map((l) => ({
      label: l.label,
      href: String(l.href || "").replace(/\{ip\}/g, ipAddress),
      external: l.external || false,
      primary: l.primary || false,
    }));
  } catch (error) {
    console.error(`Error processing links for ${matchedKey}:`, error);
    return [];
  }
}

/**
 * Get application metadata (name, category, credentials, etc.)
 * @param {string|object} image - Image string or object from server
 * @param {string} ipAddress - Server IP address (for credential placeholders)
 * @returns {object|null} Application metadata
 */
export function getAppMetadata(image, ipAddress) {
  if (!image) return null;

  const slug = normalizeSlug(image);
  const matchedKey = findMatchingApp(slug);

  if (!matchedKey) return null;

  const appData = hintsData[matchedKey];
  if (!appData) return null;

  const metadata = {
    key: matchedKey,
    name: appData.name || matchedKey,
    category: appData.category || "Application",
    defaultUsername: appData.defaultUsername || null,
    requiresPassword: appData.requiresPassword || false,
    customIcon: appData.customIcon || null,
    passwordCommand: appData.passwordCommand || null,
  };

  // Process credentials if available
  if (appData.credentials && ipAddress) {
    metadata.credentials = {};
    Object.keys(appData.credentials).forEach((key) => {
      metadata.credentials[key] = String(appData.credentials[key]).replace(
        /\{ip\}/g,
        ipAddress
      );
    });
  }

  return metadata;
}

/**
 * Check if an image has app hints available
 * @param {string|object} image - Image string or object from server
 * @returns {boolean} True if hints are available
 */
export function hasAppHints(image) {
  if (!image) return false;
  const slug = normalizeSlug(image);
  return findMatchingApp(slug) !== null;
}

/**
 * Test/debug function to see how an image name would be matched
 * Useful for debugging and testing new image formats
 * @param {string|object} imageNameOrObject - Image name string or image object
 * @returns {object} Debug information about the matching process
 */
export function debugImageMatch(imageNameOrObject) {
  const image =
    typeof imageNameOrObject === "string"
      ? { name: imageNameOrObject }
      : imageNameOrObject;

  const raw = image?.slug || image?.name || image?.description || "";
  const normalized = normalizeSlug(image);
  const keywords = extractAppKeywords(normalized);
  const matchedKey = findMatchingApp(normalized);
  const metadata = matchedKey ? getAppMetadata(image, "0.0.0.0") : null;

  return {
    raw,
    normalized,
    keywords,
    matchedKey,
    matched: !!matchedKey,
    appName: metadata?.name || null,
    category: metadata?.category || null,
  };
}

/**
 * Get list of all supported applications
 * @returns {Array} Array of app info objects
 */
export function getSupportedApps() {
  return Object.keys(hintsData)
    .map((key) => ({
      key,
      name: hintsData[key].name,
      category: hintsData[key].category || "Other",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default getAppQuickLinks;
